import { Storage } from '@lightningjs/sdk'
import { PlayerDataModel } from '../pages/player'
import { getUserData } from '../utils/authentication'

interface ContinueWatchingItem {
  field: number //set to 0
  app_id: string //set to application ID
  app_icon: string // application icon URL
  app_name: string // application name
  content_id: string // content ID
  payload: string // deeplink to content’s page
  image_url: string // thumbnail image url
  content_title: string // content title
  sub_title: string // content subtitle
  description: string // content description
  rate: string // content rating info
  genre?: string // genre of the content up to 5
  release: number // year the content was released
  duration?: number // total play time in seconds
  playback?: number // played time in seconds
  expiry?: number // expiry time in second (epoch time)
  timestamp: number // last watched time (epoch, in seconds)
}

interface ContinueWatchingDB {
  [userId: string]: Array<ContinueWatchingItem>
}

const GUEST_ID = 'guest'
const CW_STORAGE_KEY = 'cw'
const APP_ID = 'K2WI8U2JRM.FloSports' // TODO: Populate this from config.xml
const baseCWItem = {
  field: 0,
  app_id: APP_ID,
  app_name: 'FloSports',
  app_icon: `/opt/share/webappservice/apps_icon/all/${APP_ID}/icon.png`,
  rate: 'G',
}
const ANALYTICS_PARAMS = [
  { param: 'entity_id', paramType: 'number' },
  { param: 'site_name' },
  { param: 'collection_slug' },
]
const VIDEO_ANALYTICS_PARAMS = [
  { param: 'event_name' },
  { param: 'vertical_name' },
  { param: 'event_id', paramType: 'number' },
  { param: 'viewing_mode' },
]
const CONTENT_METADATA_PARAMS = [
  { param: 'no_audio', paramType: 'boolean' },
  { param: 'short_title' },
  { param: 'stream_name' },
  { param: 'playlist' },
  { param: 'site_id', paramType: 'number' },
  { param: 'type' },
]

const CW_URL = 'http://localhost:9013/?module=EBHelper&func='

export class SamsungContinueWatching {
  private cwList: Array<ContinueWatchingItem>
  private activeUserId: string

  private retrieveContinueWatching() {
    if (!this.activeUserId) {
      this.activeUserId = getUserData()?.user_id?.toString() ?? GUEST_ID
    }

    if (!this.cwList) {
      let cwDB: ContinueWatchingDB = Storage.get(CW_STORAGE_KEY)

      if (!cwDB) {
        Storage.set(CW_STORAGE_KEY, {})
        cwDB = {}
      }

      if (!cwDB[this.activeUserId]) {
        this.cwList = []
      } else {
        this.cwList = cwDB[this.activeUserId]
      }
    }
  }

  public async restoreContinueWatching() {
    this.retrieveContinueWatching()

    try {
      if (this.cwList.length > 0 && this.activeUserId !== GUEST_ID) {
        for (let i = 0; i < this.cwList.length; i++) {
          const cw = this.cwList[i]
          await this.addItemToCW(cw)
        }
      }
    } catch (e) {
      console.log(
        `There was a problem restoring the Continue Watching list on the TV for user ${this.activeUserId}. Reason: `,
        e,
      )
    }
  }

  public async clearContinueWatchingList() {
    this.activeUserId = null
    this.cwList = null

    try {
      await this.deleteFromCW()
    } catch (e) {
      console.log(
        'There was a problem clearing the Continue Watching list from the TV. Reason: ',
        e,
      )
    }
  }

  private createCWItem(playerData: PlayerDataModel, currentTime = 0): ContinueWatchingItem {
    const cwItem: ContinueWatchingItem = {
      ...baseCWItem,
      content_id: playerData?.node_id?.toString(),
      payload: this.toDeeplinkPayload(playerData, currentTime),
      image_url: playerData?.imgUrl,
      content_title: playerData?.content_metadata?.title,
      sub_title: playerData?.subtitle,
      description: playerData?.content_metadata?.title,
      release: Number(playerData?.content_metadata?.start_date?.substring(0, 4)),
      duration: playerData?.content_metadata?.duration,
      playback: currentTime,
      timestamp: Math.trunc(Date.now() / 1000),
    }

    Object.keys(cwItem).forEach((key) => {
      cwItem[key] = encodeURIComponent(cwItem[key])
    })

    return cwItem
  }

  public addOrUpdateItem(playerData: PlayerDataModel, currentTime: number) {
    if (!this.cwList || !this.activeUserId) {
      this.retrieveContinueWatching()
    }

    if (this.activeUserId === GUEST_ID) {
      return
    }

    const { node_id } = playerData
    const itemExists = this.cwList.find((cw) => cw.content_id === node_id.toString())

    if (itemExists) {
      void this.updateItem(playerData, currentTime)
    } else {
      void this.addItem(playerData, currentTime)
    }
  }

  private async addItem(playerData: PlayerDataModel, currentTime: number) {
    const cwItem = this.createCWItem(playerData, currentTime)

    try {
      if (this.cwList.length === 5) {
        const itemToDelete = this.cwList[this.cwList.length - 1]
        await this.deleteItem(itemToDelete)
      }

      await this.addItemToCW(cwItem)
      this.cwList.unshift(cwItem)
      this.persistContinueWatching()
    } catch (e) {
      console.error(
        'There was a problem adding the element to the Continue Watching list in the TV. Reason: ',
        e,
      )
    }
  }

  private async addItemToCW(cwItem: ContinueWatchingItem) {
    const response = await fetch(`${CW_URL}AddItem`, {
      headers: new Headers(cwItem as any),
    })

    if (!response.ok) {
      throw response
    }
  }

  public async deleteItem(itemData: ContinueWatchingItem | PlayerDataModel) {
    const cwItem: ContinueWatchingItem =
      'content_id' in itemData ? itemData : this.createCWItem(itemData)

    if (!this.cwList || !this.activeUserId) {
      this.retrieveContinueWatching()
    }

    if (this.activeUserId === GUEST_ID) {
      return
    }

    try {
      const itemIndex = this.cwList.findIndex((cw) => cw.content_id === cwItem.content_id)

      if (itemIndex !== -1) {
        await this.deleteFromCW(cwItem)
        this.cwList.splice(itemIndex, 1)
      } else {
        console.warn(
          `Item ${cwItem?.content_id} attempted to be deleted from Continue Watching list in TV does not exist`,
        )
      }
    } catch (e) {
      console.error(
        'There was a problem deleting the element from the Continue Watching list in the TV. Reason: ',
        e,
      )
    }
  }

  private async deleteFromCW(cwItem?: ContinueWatchingItem) {
    const response = await fetch(`${CW_URL}DeleteItem`, {
      headers: new Headers({
        field: '0',
        app_id: APP_ID,
        content_id: cwItem ? cwItem.content_id : 'ALL',
      }),
    })

    if (!response.ok) {
      throw response
    }
  }

  private async updateItem(playerData: PlayerDataModel, currentTime: number) {
    const cwItem = this.createCWItem(playerData, currentTime)

    try {
      const itemIndex = this.cwList.findIndex((cw) => cw.content_id === cwItem.content_id)

      if (itemIndex === -1) {
        console.error(
          `Item ${cwItem?.content_id} attempted to be deleted for update from Continue Watching list in TV does not exist`,
        )
        return
      }

      const itemToDelete = this.cwList[itemIndex]
      await this.deleteItem(itemToDelete)

      await this.addItemToCW(cwItem)
      this.cwList.unshift(cwItem)
      this.persistContinueWatching()
    } catch (e) {
      console.error(
        'There was a problem adding the element for update to the Continue Watching list in the TV. Reason: ',
        e,
      )
    }
  }

  private persistContinueWatching() {
    let cwDB = Storage.get(CW_STORAGE_KEY)
    cwDB = {
      ...cwDB,
      [this.activeUserId]: this.cwList,
    }

    Storage.set(CW_STORAGE_KEY, cwDB)
  }

  private toDeeplinkPayload(playerData: PlayerDataModel, currentTime = 0): string {
    const { analytics, content_metadata } = playerData
    let queryParams = []

    if (analytics) {
      ANALYTICS_PARAMS.forEach(({ param }) => {
        if (analytics[param] !== null && analytics[param] !== undefined) {
          queryParams.push(`${param}=${encodeURIComponent(analytics[param])}`)
        }
      })
    }

    if (analytics?.video_analytics) {
      const { video_analytics } = analytics

      VIDEO_ANALYTICS_PARAMS.forEach(({ param }) => {
        if (video_analytics[param] !== null && video_analytics[param] !== undefined) {
          queryParams.push(`${param}=${encodeURIComponent(video_analytics[param])}`)
        }
      })
    }

    if (content_metadata) {
      CONTENT_METADATA_PARAMS.forEach(({ param }) => {
        if (content_metadata[param] !== null && content_metadata[param] !== undefined) {
          queryParams.push(`${param}=${encodeURIComponent(content_metadata[param])}`)
        }
      })
    }

    const progress_seconds = currentTime ?? content_metadata?.progress_seconds ?? 0
    if (typeof progress_seconds === 'number') {
      queryParams.push(`progress_seconds=${encodeURIComponent(progress_seconds)}`)
    }

    const stream_id =
      content_metadata.streams?.[0]?.stream_id ?? content_metadata.stream_id ?? analytics?.stream_id
    if (stream_id) {
      queryParams.push(`stream_id=${encodeURIComponent(stream_id)}`)
    }

    const title = content_metadata?.title ?? analytics?.video_analytics?.title ?? playerData.title
    if (title) {
      queryParams.push(`title=${encodeURIComponent(title)}`)
    }

    const node_id = content_metadata?.node_id ?? playerData.node_id
    if (node_id) {
      queryParams.push(`node_id=${encodeURIComponent(node_id)}`)
    }

    const is_premium = analytics?.is_premium ?? content_metadata?.is_premium
    if (typeof is_premium === 'boolean') {
      queryParams.push(`is_premium=${encodeURIComponent(is_premium)}`)
    }

    if (playerData.subtitle) {
      queryParams.push(`subtitle=${encodeURIComponent(playerData.subtitle)}`)
    }

    if (playerData.web_game_center_url) {
      queryParams.push(`web_game_center_url=${encodeURIComponent(playerData.web_game_center_url)}`)
    }

    if (playerData.web_brackets_url) {
      queryParams.push(`web_brackets_url=${encodeURIComponent(playerData.web_brackets_url)}`)
    }

    if (playerData.imgUrl) {
      queryParams.push(`imgUrl=${encodeURIComponent(playerData.imgUrl)}`)
    }

    const queryString = `player?${queryParams.join('&')}`
    return queryString
  }

  public fromDeeplinkPayload(payload: string): PlayerDataModel {
    const urlParams = new URLSearchParams(payload)
    const playerData = {
      authDeeplinked: true,
      analytics: null,
      type: '',
      style: 'standard' as any,
      id: null,
      content_metadata: null,
      node_id: null,
      title: '',
      playlist: null,
    }
    const analytics = {}
    const video_analytics = {}
    const content_metadata = {}

    const parseToType = (value: string, paramType: string) => {
      if (paramType === 'number') {
        return Number(value)
      }

      if (paramType === 'boolean') {
        return JSON.parse(value)
      }

      return value
    }

    ANALYTICS_PARAMS.forEach(({ param, paramType }) => {
      if (urlParams.has(param)) {
        analytics[param] = parseToType(urlParams.get(param), paramType)
      }
    })

    VIDEO_ANALYTICS_PARAMS.forEach(({ param, paramType }) => {
      if (urlParams.has(param)) {
        video_analytics[param] = parseToType(urlParams.get(param), paramType)
      }
    })

    CONTENT_METADATA_PARAMS.forEach(({ param, paramType }) => {
      if (urlParams.has(param)) {
        content_metadata[param] = parseToType(urlParams.get(param), paramType)
      }
    })

    if (urlParams.has('progress_seconds')) {
      content_metadata['progress_seconds'] = parseToType(
        urlParams.get('progress_seconds'),
        'number',
      )
    }

    if (urlParams.has('stream_id')) {
      content_metadata['stream_id'] = parseToType(urlParams.get('stream_id'), 'number')
      analytics['stream_id'] = parseToType(urlParams.get('stream_id'), 'number')
    }

    if (urlParams.has('title')) {
      content_metadata['title'] = urlParams.get('title')
      video_analytics['title'] = urlParams.get('title')
      playerData['title'] = urlParams.get('title')
    }

    if (urlParams.has('node_id')) {
      content_metadata['node_id'] = parseToType(urlParams.get('node_id'), 'number')
      playerData['node_id'] = parseToType(urlParams.get('node_id'), 'number')
    }

    if (urlParams.has('is_premium')) {
      analytics['is_premium'] = parseToType(urlParams.get('is_premium'), 'boolean')
      content_metadata['is_premium'] = parseToType(urlParams.get('is_premium'), 'boolean')
    }

    if (urlParams.has('subtitle')) {
      playerData['subtitle'] = urlParams.get('subtitle')
    }

    if (urlParams.has('web_game_center_url')) {
      playerData['web_game_center_url'] = urlParams.get('web_game_center_url')
    }

    if (urlParams.has('web_brackets_url')) {
      playerData['web_brackets_url'] = urlParams.get('web_brackets_url')
    }

    if (urlParams.has('imgUrl')) {
      playerData['imgUrl'] = urlParams.get('imgUrl')
    }

    playerData['analytics'] = analytics
    playerData['video_analytics'] = video_analytics
    playerData['content_metadata'] = content_metadata

    return playerData
  }
}
